import { Link } from 'react-router-dom';
import type { ExpertProjectSummary } from '@/types/api';
import { CHAT_STATUS, CLIENT_CHAT_STATUS, clientStatusNote, errorMessage } from '@/lib/copy';
import { useDocumentTitle } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { PageBody, PageHeader } from '@/components/layout/page';
import { Flag, StateChip } from '@/components/state-chip';
import { HiddenGlyph } from '@/components/visibility-band';
import { Button } from '@/components/ui/button';
import { Empty } from '@/components/ui/empty';
import { Panel } from '@/components/ui/panel';
import { TableSkeleton } from '@/components/ui/skeleton';
import { Table, TBody, TD, TDMono, TH, THead, TR } from '@/components/ui/table';
import { ClaimState } from '@/features/queue/claim-control';
import { useExpertProjects } from './queries';

/* ---------------------------------------------------------------------------
   Your projects.

   Not a second workflow — the queue is the job. This screen exists because a
   reviewer needs the REPO INVENTORY before reading a multi-repo diff: which
   repos a project has, what order they promote in, and which of them have branch
   protection actually enforced.

   `protection_enforced: false` is called out loudly rather than listed as a
   property, because it blocks promotion to production on its own. If protection
   could not be applied to a repo, the only thing standing between arbitrary code
   and that repo's production branch is platform Go code, and that is not enough
   to promote on.
--------------------------------------------------------------------------- */

export function ProjectsPage() {
  useDocumentTitle('Your projects');
  const projects = useExpertProjects();
  const rows = projects.data ?? [];

  return (
    <>
      <PageHeader
        title="Your projects"
        description="Every project an operator has assigned to you, with the repo inventory a multi-repo diff needs and the branch-protection flag that gates promotion to production."
      />

      <PageBody>
        <Panel>
          {projects.isLoading ? (
            <TableSkeleton rows={4} cols={5} />
          ) : projects.isError ? (
            <Empty
              title="Your projects did not load"
              body={errorMessage(projects.error.code, projects.error.message)}
              action={
                <Button size="sm" variant="secondary" onClick={() => void projects.refetch()}>
                  Retry
                </Button>
              }
            />
          ) : rows.length === 0 ? (
            <Empty
              title="No projects are assigned to you"
              body="An operator creates the expert record and assigns the projects. Until then there is nothing here and nothing in your queue."
            />
          ) : (
            <Table>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>Project</TH>
                  <TH>Status</TH>
                  <TH title="What the client is currently being shown, which is not always the real status.">
                    Client sees
                  </TH>
                  <TH>Repos</TH>
                  <TH>Checked out</TH>
                  <TH className="text-right">Action</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((project) => (
                  <ProjectRow key={project.id} project={project} />
                ))}
              </TBody>
            </Table>
          )}
        </Panel>
      </PageBody>
    </>
  );
}

function ProjectRow({ project }: { project: ExpertProjectSummary }) {
  const repos = [...(project.repos ?? [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id,
  );
  const unprotected = repos.filter((repo) => !repo.protection_enforced);
  const clientStatus = project.client_visible_status;

  return (
    <TR>
      <TD className="max-w-[20rem]">
        {project.chat_id ? (
          <Link to={`/chats/${project.chat_id}`} className="block min-w-0 hover:text-action">
            <span className="block truncate font-medium text-ink">{project.name}</span>
            <span className="mt-0.5 block truncate font-mono text-tiny text-ink-3">{project.slug}</span>
          </Link>
        ) : (
          <div className="block min-w-0">
            <span className="block truncate font-medium text-ink">{project.name}</span>
            <span className="mt-0.5 block truncate font-mono text-tiny text-ink-3">{project.slug}</span>
          </div>
        )}
      </TD>

      <TD>
        {project.chat_id ? (
          <StateChip size="sm" state={CHAT_STATUS[project.status]} />
        ) : (
          <span className="font-mono text-tiny text-ink-3">no chat yet</span>
        )}
      </TD>

      <TD>
        {clientStatus ? (
          <span
            className="inline-flex items-center gap-1.5 font-mono text-tiny text-ink-2"
            title={clientStatusNote(project.status, clientStatus)}
          >
            <HiddenGlyph className="h-[9px] w-[9px] text-hidden" />
            {CLIENT_CHAT_STATUS[clientStatus]}
          </span>
        ) : (
          <span className="font-mono text-tiny text-ink-3">—</span>
        )}
      </TD>

      <TD className="max-w-[22rem]">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {repos.length === 0 ? (
            <span className="font-mono text-tiny text-ink-3">none provisioned</span>
          ) : (
            repos.map((repo) => (
              <span
                key={repo.id}
                title={
                  repo.protection_enforced
                    ? `${repo.name} · position ${repo.position ?? 0} · branch protection enforced`
                    : `${repo.name} · position ${repo.position ?? 0} · BRANCH PROTECTION NOT ENFORCED — this blocks promotion to production`
                }
                className={cn(
                  'font-mono text-tiny',
                  repo.protection_enforced ? 'text-ink-2' : 'text-danger underline decoration-dotted',
                )}
              >
                {repo.slug}
              </span>
            ))
          )}
        </div>
        {unprotected.length > 0 ? (
          <div className="mt-1">
            <Flag
              label={`${unprotected.length} unprotected`}
              tone="danger"
              title="Branch protection is not enforced on these repos, which blocks promotion to production for the whole project."
            />
          </div>
        ) : null}
      </TD>

      <TDMono>
        <ClaimState claim={project.claim ?? null} />
      </TDMono>

      <TD className="text-right">
        {project.chat_id ? (
          <Button size="sm" variant="secondary" asChild>
            <Link to={`/chats/${project.chat_id}`}>Open review</Link>
          </Button>
        ) : (
          /* Assigned, but the client has not prompted. Saying so beats a button
             that 404s and beats the row not being here at all. */
          <span className="text-tiny text-ink-3">waiting on the client</span>
        )}
      </TD>
    </TR>
  );
}
